import { get } from 'svelte/store';

import { devices } from 'src/stores';
import { getAllCameraStorageKeys } from 'src/services/cam-cloud/recordings';
import { getSignedQuery } from 'src/services/cam-cloud/media-auth';
import { camera_storage_keys } from 'src/components/Camera/stores/camera_storage_keys';
import { clearCameraStores, hasCameraDevices } from './camera-helpers';
import { destroyAllWebRTCSessions } from './webrtc-session-manager';

/**
 * Loads every camera's encrypted `master_key` backup **history** and caches the
 * full array (newest first) per device in the {@link camera_storage_keys} store,
 * so key rotation is preserved and older recordings stay decryptable.
 */
export const loadCameraStorageKeys = async (): Promise<void> => {
	const response = await getAllCameraStorageKeys().catch((err) => {
		console.error('[Camera Cloud] Error fetching camera storage keys', err);
		return null;
	});

	if (!response) return;

	for (const [mac, entries] of Object.entries(response)) {
		const history = Array.isArray(entries)
			? entries
					.filter((entry) => entry?.blob && entry?.hmac)
					.map((entry) => ({ blob: entry.blob, hmac: entry.hmac, createdAt: entry.createdAt }))
			: [];
		if (history.length) {
			camera_storage_keys.setAll(mac, history);
		}
	}
};

/**
 * Runs the camera-side loading needed on account bootstrap: the media signed
 * query and the per-device key backups.
 *
 * @remarks
 * Gated on the account owning a camera, so camera-less accounts issue no camera
 * requests at all. Must run after the devices list is loaded.
 */
export const cameraServicesInitialLoad = async (): Promise<void> => {
	if (!hasCameraDevices(get(devices))) return;
	await Promise.all([getSignedQuery(), loadCameraStorageKeys()]);
};

/**
 * Tears down everything scoped to the current session: live streams, cached
 * camera state and the media signed query, whose `URLPrefix` does not carry over
 * to another home or account.
 *
 * @remarks
 * Must run before the socket disconnects, so WebRTC sessions can still send
 * their teardown messages.
 */
export const cameraServicesUnload = (): void => {
	destroyAllWebRTCSessions();
	clearCameraStores();
};
